import * as THREE from 'three';

/**
 * Everything the HUD draws in one frame (SPEC §3.4.1).
 *
 * The HUD is a pure function of this object plus a projector: it reads no global state, holds no
 * simulation references, and never reaches back into the flight model. That is what makes the
 * `?scene=hudtest` page possible — it fills in the same struct from sliders and gets the real
 * instrument, so the HUD can be tuned without flying.
 *
 * Angles in radians, speeds m/s, distances metres, times seconds.
 */

/** One line in the tasking block (SPEC §3.10.1). */
export interface HudObjective {
  readonly description: string;
  readonly state: string;
  readonly primary: boolean;
}

const EMPTY_OBJECTIVES: readonly HudObjective[] = [];

/** Threat and navigation classes, which decide a tag's glyph and colour. */
export const TagKind = {
  Waypoint: 'waypoint',
  Threat: 'threat',
  Target: 'target',
} as const;
export type TagKindId = (typeof TagKind)[keyof typeof TagKind];

/** A label pair anchored to the horizon at its true azimuth: designation over type. */
export interface BearingTag {
  /** Upper line, e.g. "K-2". */
  designation: string;
  /** Lower line, e.g. "FIRE POINT". */
  type: string;
  /** World position the tag points at. */
  positionM: THREE.Vector3;
  kind: TagKindId;
  /**
   * Set on a target that has already been destroyed. Absent means live.
   *
   * A wreck keeps its label — knowing what you hit is worth something — but loses its range, which
   * would otherwise count down to a building that is no longer there.
   */
  destroyed?: boolean | undefined;
}

export const RwrState = {
  Search: 'search',
  Track: 'track',
  Launch: 'launch',
} as const;
export type RwrStateId = (typeof RwrState)[keyof typeof RwrState];

/** A radar emitter on the warning receiver. Aircraft-relative bearing, radians. */
export interface RwrContact {
  /** Bearing relative to the nose, radians, positive right. */
  relativeBearingRad: number;
  state: RwrStateId;
  /** Short threat-class label, e.g. "SA". */
  label: string;
}

/** An inbound missile: a screen-edge caret plus closure-rate ticks. */
export interface MissileWarning {
  /** Bearing relative to the nose, radians, positive right. */
  relativeBearingRad: number;
  /** Closure rate, m/s. Drives the tick frequency — the proxy for time to impact. */
  closureMs: number;
}

export interface HudFrame {
  // --- Attitude
  pitchRad: number;
  bankRad: number;
  /** True heading, radians clockwise from north, [0, 2π). */
  headingRad: number;

  // --- Performance
  airspeedMs: number;
  /** Radar altitude above ground level, metres. */
  altitudeAglM: number;
  loadFactor: number;
  /**
   * Commanded throttle, 0..1 — the lever position, not the engine's response to it.
   *
   * Commanded rather than actual because the lever is the part the player cannot otherwise see:
   * it moves the instant a key is pressed, whereas thrust lags it by 0.8 s and is already legible
   * through airspeed.
   */
  throttle: number;
  /** Stall speed at the current load factor, m/s — drives the amber and red airspeed bands. */
  stallSpeedMs: number;
  /** Never-exceed speed, m/s — drives the overspeed band at the top of the same readout. */
  vneMs: number;

  // --- Mission (SPEC §3.10)
  /** Objective lines for the tasking block. */
  objectives: readonly HudObjective[];
  /** Current radio call, subtitled. Empty when nothing is being said. */
  radioSpeaker: string;
  radioText: string;
  /**
   * Seconds left on the mission-start control hold, 0 once the controls are live.
   *
   * Shown, not silent: a lock the player cannot see is indistinguishable from a broken game.
   */
  controlLockS: number;

  /** Remaining endurance, seconds. */
  enduranceS: number;
  /** 0..1. Falls with range, occlusion and jamming (SPEC §3.3.2 item 6). */
  linkQuality: number;
  /**
   * Camera gain in stops, or null when no readout is wanted. Non-null only while auto-exposure is
   * switched off: SPEC §3.3.2 requires a compensating readout for the two effects that carry
   * gameplay signal, and this is the exposure half of that (link quality already has its bar).
   */
  exposureStops: number | null;
  chaffRemaining: number;
  flaresRemaining: number;

  // --- World-anchored symbology
  /** Unit direction of the velocity vector in world space; drives the flight path marker. */
  velocityDirection: THREE.Vector3;
  /** True when the aircraft is moving fast enough for the velocity vector to mean anything. */
  velocityValid: boolean;
  /** Designated aim point, or null when nothing is designated. */
  designationM: THREE.Vector3 | null;
  bearingTags: readonly BearingTag[];

  // --- Threats
  rwrContacts: readonly RwrContact[];
  missileWarnings: readonly MissileWarning[];

  // --- Presentation
  /** Simulated time, seconds. Drives every blink and flash, so they stay deterministic. */
  simTimeS: number;
  /** Jamming severity, 0..1. See HudDegradation. */
  jamming: number;
}

/** An empty frame, for the test scene and for tests. */
export function createHudFrame(): HudFrame {
  return {
    pitchRad: 0,
    bankRad: 0,
    headingRad: 0,
    airspeedMs: 0,
    altitudeAglM: 0,
    loadFactor: 1,
    throttle: 0,
    stallSpeedMs: 0,
    vneMs: Number.POSITIVE_INFINITY,
    objectives: EMPTY_OBJECTIVES,
    radioSpeaker: '',
    radioText: '',
    controlLockS: 0,
    enduranceS: 0,
    linkQuality: 1,
    exposureStops: null,
    chaffRemaining: 0,
    flaresRemaining: 0,
    velocityDirection: new THREE.Vector3(0, 0, -1),
    velocityValid: false,
    designationM: null,
    bearingTags: [],
    rwrContacts: [],
    missileWarnings: [],
    simTimeS: 0,
    jamming: 0,
  };
}
